// assistantMainThread.js — THE MAIN THREAD (AI_ASSISTANT_SPEC v1 §1 + §8 and
// v1.1 §12.2; Nika's pick 6 A, the DEFAULT placement). Nika 2026-09-28: "when I
// ask something to AI, it should use generative capabilities and render into
// MainColumn … lets try MainColumn as AI thread as default option"; 2026-09-29:
// "make main thread ai as default" — main thread = "Everywhere".
//
// MODE × PLACEMENT. The mode (`assistantMode`, functions/assistantMode.js) is
// WHAT the AI produces; the placement (`assistantDock`, functions/
// assistantDock.js) is WHERE it shows. With the main placement
// (`assistantDock === 'main'`) on ANY route:
//   dashboard     → a GENERATED BOARD over the main area (functions/
//                   assistantBoard.js — the ai.boards path, one fast call);
//   presentation  → a LIVE DECK over the main area (functions/assistantDeck.js,
//                   the same path's deck shape);
//   assistant     → the MAIN THREAD: the SAME general-scope conversation the
//   tools           sidebar shows (`root.chatMessages`, the chat agent with every
//   apps            tool) — a reading column for text, full-width rows for blocks
//                   (components/AssistantMainThread.js).
// The MAIN AREA is the home's grid area on the home (pages/main.js mounts the
// views there) and the page sheet everywhere else (components/
// AssistantMainLayer.js lays the same views over the page; the page stays
// mounted underneath and keeps its state). A page SHEET open in the PageSlot
// covers the main area, so a send there keeps the compact path (the reasoning
// pill over the sheet). Another placement (sidebar, minimized) takes today's
// path: the drawer or the pill. The mode rides every dispatch either way
// (simoneAiTransport → `context.assistantMode`).
//
// THE FLAG. `root._homeThreadOpen` says the main thread is up (the name
// predates §12.2; it holds for every route now), `root._mainThreadPath` the
// route it opened on. The reasoning pill and the suggestion island step aside
// while the flag is set (shared/_simone), so the thread is the turn's ONE
// surface. It is cleared whenever the thread cannot show: Esc / Back, Open in
// sidebar, a board or a deck taking the view, the placement leaving 'main', a
// NAVIGATION (the page the person went to shows — the conversation stays in
// the sidebar and the thread chips).
//
// Frank note: el.call serializes each registered export on its own (FA204),
// so every export here is self-contained — the few literals are restated.

// Where a composer send goes: 'fly' | 'board' | 'deck' | 'thread' | null
// (today's path). FLY (docs/FLY_MODE.md) is its own canvas in every placement. The FIVE modes of AI_ASSISTANT_SPEC §12.1 take two brains: DASHBOARD
// and PRESENTATION are the ai.boards path (one fast call, no tools — a board,
// a live deck); ASSISTANT, TOOLS and APPS are the chat agent with every tool
// (the main thread). A board or a deck the service cannot take yet is a
// thread turn (the server answers it like Assistant, deck contract §7). Any
// route (§12.2); a page sheet open in the PageSlot keeps today's path — the
// sheet covers the main area, and the reasoning pill carries the turn over it.
// `askMode` — one ask's own output (the focus panel's Summarize / Build a
// board, functions/aiPanel.js) routes that ask; absent, the person's mode.
export const assistantSendRoute = function assistantSendRoute(root, askMode) {
  const r = root && typeof root === 'object' ? root : null
  if (!r) return null
  if (r._canvasSlot || r._canvasSlotSecond) return null
  const modes = ['assistant', 'dashboard', 'tools', 'apps', 'presentation', 'fly']
  const mode = modes.includes(askMode)
    ? askMode
    : modes.includes(r.assistantMode)
      ? r.assistantMode
      : 'assistant'
  const fns = this && this.context && this.context.functions
  // FLY is a canvas of pages in the main area by nature ("the conversation IS
  // the site") — whatever the placement, once the board service answers here
  // (`_aiBoardsAvailable`; the fly page engine falls back to a board on a
  // server without its shape).
  if (
    mode === 'fly' &&
    r._aiBoardsAvailable === true &&
    !!fns &&
    typeof fns.assistantFlyGenerate === 'function'
  ) {
    return 'fly'
  }
  if (r.assistantDock !== 'main') return null
  const boards = r._aiBoardsAvailable === true && !!fns
  if (mode === 'dashboard' && boards && typeof fns.assistantBoardGenerate === 'function') {
    return 'board'
  }
  if (mode === 'presentation' && boards && typeof fns.assistantDeckGenerate === 'function') {
    return 'deck'
  }
  return 'thread'
}

// The view's gate: the flag and the main placement, on the route the thread
// opened on (`_mainThreadPath`; a flag with no recorded route holds wherever
// it is). An EMPTY thread shows too — an opener in Page mode (§12.2) opens it
// before anything is asked, and the view says where to ask. Every key is read
// up front so a reactive caller subscribes to all of them whichever branch
// returns.
export const assistantMainThreadOpen = function assistantMainThreadOpen(root) {
  const r = root && typeof root === 'object' ? root : {}
  const open = r._homeThreadOpen === true
  const dock = r.assistantDock
  const raw = typeof r.currentPath === 'string' && r.currentPath ? r.currentPath : '/'
  const at = r._mainThreadPath
  if (!open || dock !== 'main') return false
  if (typeof at !== 'string' || !at) return true
  const norm = (p) => {
    const s = typeof this.call === 'function' ? this.call('stripWorkspacePrefix', p) : p
    const v = typeof s === 'string' ? s : p
    return v === '' ? '/' : v.length > 1 ? v.replace(/\/+$/, '') : v
  }
  return norm(raw) === norm(at)
}

// WHERE the main views show for a root: 'home' (the home's grid area,
// pages/main.js) or 'over' (the page sheet, components/AssistantMainLayer.js).
export const assistantMainHost = function assistantMainHost(root) {
  const r = root && typeof root === 'object' ? root : {}
  const raw = typeof r.currentPath === 'string' && r.currentPath ? r.currentPath : '/'
  const stripped = typeof this.call === 'function' ? this.call('stripWorkspacePrefix', raw) : raw
  const path = typeof stripped === 'string' ? stripped : raw
  return path === '/' || path === '' ? 'home' : 'over'
}

// THE LAYER over a page (components/AssistantMainLayer.js): up while one of
// the main views — the thread, a generated board, a live deck — holds the
// main area of a route that is not the home (the home keeps them in its grid
// area). Each view is asked through its own gate when its function is
// registered. Every key is read up front (reactive callers).
export const assistantMainLayerShown = function assistantMainLayerShown(root) {
  const r = root && typeof root === 'object' ? root : {}
  const host = this.call('assistantMainHost', r)
  const thread = !!this.call('assistantMainThreadOpen', r)
  const fns = this && this.context && this.context.functions
  const board =
    fns && typeof fns.assistantBoardShown === 'function'
      ? !!this.call('assistantBoardShown', r)
      : false
  const deck =
    fns && typeof fns.assistantDeckShown === 'function'
      ? !!this.call('assistantDeckShown', r)
      : false
  const fly =
    fns && typeof fns.assistantFlyShown === 'function' ? !!this.call('assistantFlyShown', r) : false
  // A call's theater (meet functions/meetFly.js — registered with the call's
  // meet context): the call's Fly pages over the call.
  const call =
    fns && typeof fns.meetFlyTheaterShown === 'function'
      ? !!this.call('meetFlyTheaterShown', r)
      : false
  return host === 'over' && (thread || board || deck || fly || call)
}

// The shell overlay reader's question (shared functions/shellOverlayOpen.js):
// is the main layer the TOP layer over the page right now? A page sheet open
// in the PageSlot sits above the layer, so the layer is not the top then —
// the sheet's own Esc comes first. `this` = the host element.
export const assistantMainLayerOpen = function assistantMainLayerOpen(host) {
  const origin = host || this
  const st = origin && origin.state
  const root = (st && st.root) || st || null
  if (!root || root._canvasSlot || root._canvasSlotSecond) return false
  return !!(
    origin &&
    typeof origin.call === 'function' &&
    origin.call('assistantMainLayerShown', root)
  )
}

// Show the thread for a send: a generated board or a deck on screen steps
// aside (it stays in the thread chips / the saved row), so ONE view holds the
// main area and Esc returns to the page — and the thread gets its
// conversation before the send goes (assistantMainThreadSession). The route it
// opens on is kept: a navigation closes it (assistantMainThreadSync).
export const assistantMainThreadShow = function assistantMainThreadShow() {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const fns = this.context && this.context.functions
  if (fns && typeof fns.assistantBoardDismiss === 'function') this.call('assistantBoardDismiss')
  if (fns && typeof fns.assistantDeckDismiss === 'function') this.call('assistantDeckDismiss')
  if (fns && typeof fns.assistantFlyClose === 'function') this.call('assistantFlyClose')
  // A call's theater (meet functions/meetFly.js) steps aside too: the call
  // comes back under the thread's layer only when the thread closes.
  if (fns && typeof fns.meetFlyTheater === 'function') this.call('meetFlyTheater', false)
  const path = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  if (root._homeThreadOpen !== true || root._mainThreadPath !== path) {
    root.update({ _homeThreadOpen: true, _mainThreadPath: path }, { preventFetch: true })
  }
  // A send opened it: the composer is the opener (Esc returns there).
  const app = typeof this.getRoot === 'function' ? this.getRoot() : null
  if (app) app.__mainThreadOpener = null
  this.call('assistantMainThreadSession')
  return true
}

// THE MAIN THREAD'S CONVERSATION (qa round 9, 2026-09-29: "What needs me
// today?" took ~230-260 s in the main thread). The drawer's Chat restores the
// remembered thread when it MOUNTS (restoreActiveThread); the main thread never
// mounts that Chat, so on a fresh page its first send carried NO conversation
// (conversationId null, newConversation false) and the SDK appended it to its
// CACHED last-used conversation — a hidden, possibly long history under a
// visually empty thread (the "stale answers in a visually-empty chat" trap
// functions/_simone/sessions.js newSession documents). A send with no active
// session and nothing on screen starts a NEW conversation: a local 's-…'
// session (ensureActiveSession), which the transport sends as
// `newConversation: true`, and the turn's done frame re-points to the real id.
// A thread already active, or already on screen, is continued.
export const assistantMainThreadSession = function assistantMainThreadSession() {
  const root = this && this.state && this.state.root
  if (!root) return null
  if (root.activeSessionId) return root.activeSessionId
  const list = Array.isArray(root.chatMessages) ? root.chatMessages : []
  if (list.length) return null
  const fns = this.context && this.context.functions
  if (!fns || typeof fns.ensureActiveSession !== 'function') return null
  this.call('ensureActiveSession')
  return root.activeSessionId || null
}

// THE CONVERSATION AN OPENER SHOWS (ai-priya, dev 2026-10-28: two Fly asks,
// then "Open in sidebar" beside her board opened an empty "New chat"). A Fly
// ask writes its page into the Fly session's thread (functions/assistantFly.js
// — the session IS an assistant thread), never into the chat, so the chat held
// no conversation and every opener showed the starters while her thread waited
// in Chat history. An opener asks for the conversation, never for a new one:
// in Fly (and a stored Dashboard, which reads as Pages) or with the Fly canvas
// on screen, the conversation is the Fly session's thread — after a reload,
// the stored one, brought back the way the canvas's own mount brings it. A
// chat that holds a conversation keeps it: an open thread, a turn on its way,
// a New chat the person pressed (its local `s-…` id). In the chat views the
// remembered thread stays the conversation (_simone/sessions.js
// restoreActiveThread). The shell's open edge runs this BEFORE the placement
// moves (app.js `_syncShellRoute`), so the view it reads is the one the person
// opened from. Returns the id it opens, or null.
export const assistantOpenThread = function assistantOpenThread(root) {
  const r = root && typeof root === 'object' ? root : null
  if (!r) return null
  const fns = (this && this.context && this.context.functions) || {}
  const has = (name) => typeof fns[name] === 'function'
  const list = Array.isArray(r.chatMessages) ? r.chatMessages : []
  if (r.activeSessionId || list.length) return null
  if (!has('assistantFlySession') || !has('switchSession')) return null
  const mode = r.assistantMode
  const view = has('assistantEffectiveView') ? this.call('assistantEffectiveView', r) : null
  if (mode !== 'fly' && mode !== 'dashboard' && view !== 'pages') return null
  if (!this.call('assistantFlySession', r) && has('assistantFlyRestore')) {
    this.call('assistantFlyRestore', r)
  }
  const fly = this.call('assistantFlySession', r)
  const id = fly && typeof fly.threadId === 'string' && fly.threadId ? fly.threadId : null
  if (!id) return null
  // The switch reads the thread from the server and logs its own failure; a
  // thread it cannot read leaves the chat as it was.
  const loading = this.call('switchSession', id)
  if (loading && typeof loading.catch === 'function') loading.catch(() => null)
  return id
}

// Esc / Back: the thread crossfades out, the page (or the home board) is back,
// the conversation stays (the sidebar and the thread chips still hold it).
// Never changes the mode or the placement.
export const assistantMainThreadClose = function assistantMainThreadClose(opts) {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  if (root._homeThreadOpen === true || root._mainThreadPath) {
    root.update({ _homeThreadOpen: false, _mainThreadPath: null }, { preventFetch: true })
  }
  // Focus: back to the OPENER when a page button opened the thread (the
  // shell's open edge records it — app.js), else to the composer.
  const app = typeof this.getRoot === 'function' ? this.getRoot() : null
  const opener = app ? app.__mainThreadOpener : null
  if (app) app.__mainThreadOpener = null
  if (opts && opts.focusComposer) {
    const doc = opener && opener.ownerDocument
    if (
      opener &&
      opener.isConnected !== false &&
      typeof opener.focus === 'function' &&
      (!doc || opener !== doc.body)
    ) {
      try {
        opener.focus({ preventScroll: true })
      } catch (_) {
        /* the opener cannot take focus — the composer below */
      }
      // focus() never throws for an element the browser refuses (hidden,
      // disabled, inert) — it leaves focus where it was. Only an opener that
      // really HOLDS focus now ends here; otherwise the composer takes it.
      if (doc && doc.activeElement === opener) return true
    }
    const fns = this.context && this.context.functions
    if (fns && typeof fns.assistantBoardFocus === 'function')
      this.call('assistantBoardFocus', 'composer')
  }
  return true
}

// Keep the flag honest: it can only be up with the main placement, on the
// route the thread opened on — the placement leaving 'main' or a NAVIGATION
// clears it (the page the person went to shows; the pill and the island come
// back). Both views run it on every placement / route change. Returns true
// when it cleared the flag.
export const assistantMainThreadSync = function assistantMainThreadSync(root) {
  const r = root && typeof root === 'object' ? root : null
  if (!r || r._homeThreadOpen !== true || typeof r.update !== 'function') return false
  const raw = typeof r.currentPath === 'string' && r.currentPath ? r.currentPath : '/'
  const at = r._mainThreadPath
  const norm = (p) => {
    const s = typeof this.call === 'function' ? this.call('stripWorkspacePrefix', p) : p
    const v = typeof s === 'string' ? s : p
    return v === '' ? '/' : v.length > 1 ? v.replace(/\/+$/, '') : v
  }
  const moved = typeof at === 'string' && !!at && norm(raw) !== norm(at)
  if (r.assistantDock === 'main' && !moved) return false
  r.update({ _homeThreadOpen: false, _mainThreadPath: null }, { preventFetch: true })
  return true
}

// "Open in sidebar": the SAME thread moves to the docked panel. In Page mode
// it is a SESSION promotion to the last side this device docked to — written
// here, because an opener in Page mode now opens the main thread (§12.2,
// functions/assistantDock.js promoteAssistantDockForOpen); closing the drawer
// returns to Page (the shell's close edge, restoreAssistantDockOnClose).
export const assistantThreadToSidebar = function assistantThreadToSidebar() {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const patch = { _homeThreadOpen: false, _mainThreadPath: null, assistantOpen: true }
  if (root.assistantDock === 'main') {
    const side = this.call('lastAssistantSide')
    patch.assistantDock = side === 'left' ? 'left' : 'right'
    patch._assistantDockPromotedFrom = 'main'
  }
  root.update(patch)
  return true
}

// "Open in main": the SAME thread moves back to the main area — on ANY route
// (§12.2), over the page the person is on. A drawer promoted out of the main
// placement closes back to it (the shell's close edge); a sidebar the person
// chose becomes the main placement through the dock writer.
export const assistantThreadToMain = function assistantThreadToMain() {
  const root = this && this.state && this.state.root
  if (!root || typeof root.update !== 'function') return false
  const promoted = root._assistantDockPromotedFrom === 'main'
  if (!promoted && root.assistantDock !== 'main') this.call('setAssistantDock', 'main')
  const fns = this.context && this.context.functions
  if (fns && typeof fns.assistantBoardDismiss === 'function') this.call('assistantBoardDismiss')
  if (fns && typeof fns.assistantDeckDismiss === 'function') this.call('assistantDeckDismiss')
  // ONE view holds the main area: the Fly canvas and a call's theater step
  // aside too, as assistantMainThreadShow does — a thread link (`?ai=`) on a
  // Fly session drew the thread over the open canvas, the same answers twice
  // (Nika 2026-10-18: "it duplicates ui").
  if (fns && typeof fns.assistantFlyClose === 'function') this.call('assistantFlyClose')
  if (fns && typeof fns.meetFlyTheater === 'function') this.call('meetFlyTheater', false)
  const path = typeof root.currentPath === 'string' && root.currentPath ? root.currentPath : '/'
  root.update({ assistantOpen: false, _homeThreadOpen: true, _mainThreadPath: path })
  // The drawer that held focus is gone: focus goes to the composer the thread
  // answers through (never the body). Esc then returns there too.
  const app = typeof this.getRoot === 'function' ? this.getRoot() : null
  if (app) app.__mainThreadOpener = null
  if (fns && typeof fns.assistantBoardFocus === 'function')
    this.call('assistantBoardFocus', 'composer')
  return true
}

// Is "Open in main" on offer here (the sidebar header's move)? A thread with
// something in it — on any route.
export const assistantThreadCanMoveToMain = function assistantThreadCanMoveToMain(root) {
  const r = root && typeof root === 'object' ? root : {}
  const list = r.chatMessages
  return Array.isArray(list) && list.length > 0
}

// The thread's rows: every message once, keyed by its id, with the grouping
// the sidebar uses (a continuation of one author's run drops its header;
// `_last` = the newest row, its follow-ups at full strength) — and the WIDE
// blocks lifted out of the reading column into their own full-board-width row
// (`_wide`) — every type AssistantThreadBlock draws (the shared ChatCard's
// PvCardBlock list): an answer view, a records block, a board, an app's widget
// or view, an app's form, web sources. One rule, so every block has one width
// (__tests__/assistantMainThread.test.js reads the ChatCard list). Every derived key is
// written on every row, true or false, because childrenAs:'state' updates
// MERGE (a stale `_last: true` would survive otherwise).
// A message that draws nothing has no row: an assistant row with no words, no
// stream, no cards and no steps (the shared row hides it — ChatMessage's own
// `if`), so it never leaves an empty slot in the column. A setup note with a
// plan stays: its row draws the plan card (components/WorkspaceSetupPlan.js).
// The key keeps the message's place in the list (assistantThreadAsks reads it).
export const assistantThreadRows = function assistantThreadRows(list, cache) {
  const WIDE = ['view', 'records', 'board', 'widget', 'form', 'sources', 'capabilities']
  const all = Array.isArray(list) ? list.filter((m) => m && typeof m === 'object') : []
  const draws = (m) =>
    m.role !== 'assistant' ||
    !!m.text ||
    !!m.streaming ||
    (Array.isArray(m.cards) && m.cards.length > 0) ||
    (Array.isArray(m.tools) && m.tools.length > 0) ||
    !!(m.metadata && m.metadata.kind === 'note' && m.metadata.plan)
  const at = []
  const rows = []
  all.forEach((m, i) => {
    if (!draws(m)) return
    at.push(i)
    rows.push(m)
  })
  const previous = cache?.threadRows || []
  const next = []
  let prevRole = null
  const result = rows.map((m, i) => {
    const grouped = m.role === prevRole
    prevRole = m.role
    const key = String(m.id || 'm' + at[i])
    const last = i === rows.length - 1
    const hit = previous[i]
    const fields = Object.keys(m)
    const cards = Array.isArray(m.cards) ? m.cards : []
    const sameCards =
      hit &&
      hit.row.key === key &&
      hit.cardSnapshot.length === cards.length &&
      cards.every((card, index) => Object.is(card, hit.cardSnapshot[index]))
    // Streaming replaces only its current message. Retain settled rows and
    // block arrays so a delta cannot reconcile every chart in the thread.
    // Compare a shallow snapshot too: a caller may update a message in place.
    if (
      hit &&
      hit.row.key === key &&
      hit.row._last === last &&
      hit.row._grouped === grouped &&
      sameCards &&
      fields.length === Object.keys(hit.source).length &&
      fields.every((field) => Object.is(m[field], hit.source[field]))
    ) {
      next.push(hit)
      return hit.row
    }
    const wide = sameCards ? hit.row._wide : cards.filter((c) => c && WIDE.includes(c.type))
    const row = {
      ...m,
      key,
      cards: sameCards
        ? hit.row.cards
        : wide.length
          ? cards.filter((c) => !(c && WIDE.includes(c.type)))
          : cards,
      _wide: wide,
      _grouped: grouped,
      _last: last
    }
    next.push({ source: { ...m }, cardSnapshot: cards.slice(), row })
    return row
  })
  if (cache) {
    const settled = cache.threadRowResult
    if (settled?.length === result.length && result.every((row, i) => row === settled[i])) {
      return settled
    }
    cache.threadRows = next
    cache.threadRowResult = result
  }
  return result
}

// The thread head's micro-label: the VIEW's word and the number of questions,
// one bundle string with named placeholders (word order stays translatable).
// The view is the ONE reader the composer's placeholder and the Ask menu read
// too (functions/assistantAskRoute.js assistantEffectiveView): a thread on
// screen is "Chat · 7 questions" whatever mode the home's tab left — it said
// "Dashboard · 7 questions" over a chat in Nika's debugged thread (2026-10-27).
// A host without that reader keeps the mode's word.
export const assistantThreadMicro = function assistantThreadMicro(root) {
  const r = root && typeof root === 'object' ? root : {}
  const words = {
    assistant: 'workspace.assistantThread.modeAssistant',
    dashboard: 'workspace.assistantThread.modeDashboard',
    tools: 'workspace.assistantThread.modeTools',
    apps: 'workspace.assistantThread.modeApps',
    presentation: 'workspace.assistantThread.modePresentation'
  }
  const views = {
    chat: 'workspace.homeView.chat',
    home: 'workspace.homeView.chat',
    pages: 'canvas.navbar.pages.panelTitle',
    build: 'workspace.assistantThread.modeApps',
    present: 'workspace.assistantThread.modePresentation'
  }
  const mode = words[r.assistantMode] ? r.assistantMode : 'assistant'
  const fns = (this.context && this.context.functions) || {}
  const view =
    typeof fns.assistantEffectiveView === 'function' ? this.call('assistantEffectiveView', r) : null
  const list = Array.isArray(r.chatMessages) ? r.chatMessages : []
  const asks = list.filter((m) => m && m.role === 'user').length
  const word = String(this.call('polyglot', views[view] || words[mode]))
  const line = String(
    this.call(
      'polyglot',
      asks === 1 ? 'workspace.assistantThread.microOne' : 'workspace.assistantThread.micro'
    )
  )
  return line.replace('{mode}', word).replace('{count}', String(asks))
}

// Esc closes the thread and returns focus to the composer — a row of the shell
// keyboard registry (functions/shortcuts.js) in the board group of the ? sheet,
// registered when the view mounts and taken out when it is removed. Esc is a
// named key: the registry stands it down while an overlay owns the keyboard.
// TWO views register it — the home's (pages/main.js) and the page layer's
// (components/AssistantMainLayer.js, `mainHost: 'over'`). The registry keeps
// ONE row per id and a row's removal takes only its own record, so each view
// keeps its own id: the layer's row is unlabelled (the ? sheet lists the close
// once) and outlives the home page going away. Either row's close is the same
// close.
export const assistantMainThreadShortcuts = function assistantMainThreadShortcuts(el) {
  const view = el || this
  if (!view || typeof view.call !== 'function') return () => false
  const over = view.mainHost === 'over'
  view.call('listShortcuts')
  const off = view.call('registerShortcut', {
    id: over ? 'assistantThreadCloseOver' : 'assistantThreadClose',
    keys: 'escape',
    label: over ? null : 'workspace.assistantThread.shortcutClose',
    group: 'shell.headerTabs.dashboard',
    groupOrder: 3,
    order: 8,
    // A page SHEET above the thread (the AI opened a page in split during the
    // turn) is the top layer: the shell's own Esc row closes it first.
    when: (h, root) => {
      const node = view.node
      if (!node || node.isConnected === false) return false
      if (root && (root._canvasSlot || root._canvasSlotSecond)) return false
      return !!view.call('assistantMainThreadOpen', root)
    },
    run: () => view.call('assistantMainThreadClose', { focusComposer: true })
  })
  const stop = () => (typeof off === 'function' ? !!off() : false)
  view.__threadShortcutsOff = stop
  return stop
}
